/**
 * A KÖTEG MEGTERVEZÉSE — mi mehet el, mi nem, és hány kérésben.
 *
 * MIÉRT KELL EGYÁLTALÁN. A szerver a KÉRÉST utasítja el, nem a hibás fájlt:
 * a méretkorlát (20 fájl, összesen 25 MiB) túllépése 413-at ad, egyetlen
 * rossz típus pedig 415-öt — mindkettő az EGÉSZ kérésre. Vagyis egy 30 fájlos
 * ejtésből, amiben van egy `.xlsx`, kliensoldali tervezés nélkül NULLA
 * bizonylat érkezne be, és az ügyfél nem tudná, melyik fájl vitte el a
 * többit. A szerver azt üzeni, „küldd két részletben" — de a fájlválasztóban
 * nem látja a méreteket, és a MIME-típust végképp nem.
 *
 * ⚠️ EZ NEM KIKÉNYSZERÍTÉS, HANEM ELŐRELÁTÁS. A korlát a szerveren él, és ott
 * is marad. Ha a két oldal elválik, a rosszabbik eset az, hogy fölöslegesen
 * sok részletre bontunk, vagy kapunk egy 413/415-öt, amit a felület a szerver
 * saját mondatával mutat meg. Csendes adatvesztés egyik irányban sincs.
 *
 * A HÁROM KONSTANS A KÖZÖS FORRÁSBÓL JÖN — `packages/upload-limits`, a
 * szervizekbe a `scripts/shared-sync.py` másolja, `--check` módja pedig a
 * pre-commit hookban ÉS a CI-ban is fut. Korábban kézi másolat volt itt, egy
 * backend-oldali szerződés-teszttel őrizve; az áthelyezés a #8 beolvadására
 * várt (2026-09-04).
 *
 * AMI KIMARAD, AZ NÉVVEL MARAD KI. A néma elhagyás itt a legrosszabb kimenet:
 * az ügyfél megnyugodna, a bizonylat meg sehol. Ezért a kihagyott fájl OKKAL
 * EGYÜTT kerül a `skipped`-be, és a felület felsorolja.
 */

import {
  isAcceptedMimeType,
  MAX_FILES_PER_UPLOAD,
  MAX_UPLOAD_BYTES,
} from '@/contracts/generated/upload-limits';

/**
 * A RÉSZLET KORLÁTAI = A KÉRÉS KORLÁTAI. Külön nevük azért van, mert itt más
 * a szerepük: a szerveren KAPU, itt VÁGÁSI PONT. Ugyanaz a szám, két olvasat.
 */
export const MAX_FILES_PER_CHUNK = MAX_FILES_PER_UPLOAD;
export const MAX_BYTES_PER_CHUNK = MAX_UPLOAD_BYTES;

export type SkipReason =
  /** Egyedül is nagyobb a kérés-korlátnál — semmilyen részletbe nem fér bele. */
  | 'tooLarge'
  /** Nem PDF és nem fénykép; egy kérésbe téve az egészet elbuktatná. */
  | 'wrongType'
  /**
   * ELMENT, DE A SZERVER VISSZAADTA (413/415). Ez a `tooLarge`/`wrongType`
   * párja a másik oldalról: azt jelenti, hogy a KLIENS korlátai elsodródtak
   * a szerverétől — mi átengedtük, ő nem. A felhasználó felé ugyanaz a
   * mondat kell („ez nem ment át, íme a neve"), a fejlesztő felé viszont más:
   * ez azt jelenti, hogy a szerver korlátja a KÖZÖS FORRÁSON KÍVÜL mozdult
   * el (`packages/upload-limits`) — az `--check` őre azt nem látja.
   */
  | 'refused';

export interface SkippedFile {
  file: File;
  reason: SkipReason;
  /** Amit a szerver mondott — csak `refused`-nál van, és csak ha mondott valamit. */
  serverMessage?: string | null;
  /**
   * A saját mondatunk kulcsa a státuszkód alapján — a `serverMessage`
   * TARTALÉKA. A 413-nál a törzs gyakran üres (a proxy vágja el a kérést,
   * mielőtt az app válaszolna), és ilyenkor a „nem fogadta el" önmagában nem
   * mondja meg, MIÉRT — épp azt, amit az ügyfélnek tudnia kellene.
   */
  messageKey?: string;
}

export interface ChunkPlan {
  /** A küldhető részletek, a kiválasztás sorrendjében. */
  chunks: File[][];
  /** Ami el sem indul — a felület névvel sorolja fel. */
  skipped: SkippedFile[];
}

export function planChunks(files: readonly File[]): ChunkPlan {
  const chunks: File[][] = [];
  const skipped: SkippedFile[] = [];

  let current: File[] = [];
  let currentBytes = 0;

  for (const file of files) {
    // ELŐBB AZ ELEVE LEHETETLEN. Enélkül egy túl nagy vagy rossz típusú fájl
    // vagy üres részletet nyitna maga előtt, vagy — bent maradva — minden
    // részletet elrontana, amibe belekerül.
    if (!isAcceptedMimeType(file.type)) {
      skipped.push({ file, reason: 'wrongType' });
      continue;
    }
    if (file.size > MAX_BYTES_PER_CHUNK) {
      skipped.push({ file, reason: 'tooLarge' });
      continue;
    }

    const full = current.length >= MAX_FILES_PER_CHUNK;
    const wouldOverflow = currentBytes + file.size > MAX_BYTES_PER_CHUNK;

    // ⚠️ A `current.length > 0` ŐR MA ELÉRHETETLEN, ÉS SZÁNDÉKOSAN MARAD ITT.
    // Idáig csak olyan fájl jut el, ami a méret-előszűrésen átment, tehát üres
    // `current` mellett a `wouldOverflow` sosem igaz — mutációs próbával
    // mérve (2026-09-04): az őr kivétele EGY tesztet sem bukik el. Nem
    // takarítjuk el, mert az előszűrés átrendezése vagy lazítása esetén ez az
    // egyetlen dolog, ami egy ÜRES kérés elküldését megakadályozza — az pedig
    // 400-zal jönne vissza, „nincs fájl"-lal, a köteg közepén.
    if (current.length > 0 && (full || wouldOverflow)) {
      chunks.push(current);
      current = [];
      currentBytes = 0;
    }

    current.push(file);
    currentBytes += file.size;
  }

  if (current.length > 0) {
    chunks.push(current);
  }

  return { chunks, skipped };
}
